<template>
  <el-container class="app-layout-container">
    <div v-if="isMobile && sidebar.opened" class="drawer-bg" @click="handleClickOutside"></div>
    <!-- 左侧边栏 -->
    <alt-sidebar class="app-sidebar-component"/>
    <el-container class="app-main-container">
      <!-- 头部 -->
      <el-header class="app-header-component flex">
        <div class="header-left">
          <breadcrumb class="app-breadcrumb"></breadcrumb>
        </div>
        <div class="header-right">
          <!-- 全局搜索触发按钮 -->
          <div class="search-trigger-btn" @click="showSearch = true">
            <font-awesome-icon icon="search" class="search-btn-icon" />
            <span class="search-btn-text">搜索...</span>
            <span class="search-btn-kbd">
              <kbd class="kbd-key">{{ isMac ? '⌘' : 'Ctrl' }}</kbd>
              <kbd class="kbd-key">K</kbd>
            </span>
          </div>

          <!-- 游戏选择器 -->
          <div class="game-select-wrapper">
            <el-select v-model="appId" placeholder="请选择游戏" filterable @change="changeGame" size="small" class="premium-game-select">
              <el-option v-for="item in gameOptions" :key="item.id" :label="item.id + ' · ' + item.name" :value="item.id"></el-option>
            </el-select>
          </div>

          <!-- 主题切换按钮 -->
          <div class="theme-switch-btn-wrapper">
            <el-tooltip :content="currentTheme === 'dark' ? '切换为亮色模式' : '切换为暗色模式'" placement="bottom">
              <div class="theme-switch-btn" @click="toggleTheme">
                <font-awesome-icon :icon="currentTheme === 'dark' ? 'sun' : 'moon'" class="theme-icon" />
              </div>
            </el-tooltip>
          </div>

          <!-- 用户头像及个人菜单 Dropdown -->
          <el-dropdown @command="handleUserCommand" trigger="click" class="user-profile-dropdown">
            <div class="user-avatar-wrapper">
              <div class="user-avatar">
                {{ usernameInitial }}
              </div>
            </div>
            <el-dropdown-menu slot="dropdown" class="premium-dropdown-menu">
              <div class="dropdown-user-header">
                <span class="header-username">{{ username }}</span>
                <span class="header-role">系统管理员</span>
              </div>
              <el-dropdown-item command="profile">
                <font-awesome-icon icon="user" class="dropdown-item-icon" /> 个人信息
              </el-dropdown-item>
              <el-dropdown-item command="shortcuts">
                <font-awesome-icon icon="keyboard" class="dropdown-item-icon" /> 快捷键参考
              </el-dropdown-item>
              <el-dropdown-item divided command="logout" class="menu-item-logout">
                <font-awesome-icon icon="power-off" class="dropdown-item-icon text-danger" /> 退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-header>

      <!-- 页签标签栏 (TagsView) -->
      <div class="tags-view-container">
        <!-- 彻底替换 router-link 为 span 节点，手写跳转逻辑，彻底避开 router-link 内部点击拦截 bug -->
        <span
          v-for="tag in visitedViews"
          :key="tag.path"
          :class="['tag-view-item', { active: isActive(tag) }]"
          @click="handleTagClick(tag)"
          @contextmenu.prevent="openContextMenu(tag, $event)"
        >
          <font-awesome-icon v-if="tag.icon" :icon="parseIcon(tag.icon)" class="tag-icon" />
          <span class="tag-title">{{ tag.title }}</span>
          <span v-if="!isFixed(tag)" class="el-icon-close tag-close-btn" @click.prevent.stop="closeView(tag)"></span>
        </span>
      </div>

      <!-- 主体内容 -->
      <el-main class="app-main-component">
        <transition name="fade-slide" mode="out-in">
          <router-view/>
        </transition>
      </el-main>
    </el-container>

    <!-- 页签右键上下文菜单，改挂在全屏大容器下，采用 fixed 视口固定定位，防止被 tags-view-container 的 overflow-y: hidden 裁切 -->
    <ul v-show="contextMenuVisible" :style="{ left: contextMenuLeft + 'px', top: contextMenuTop + 'px' }" class="contextmenu" @click.stop>
      <li :class="{ disabled: isFixed(selectedTag) }" @click="!isFixed(selectedTag) && (closeSelectedTag(selectedTag), closeContextMenu())">
        <i class="el-icon-circle-close context-icon"></i>关闭当前
      </li>
      <li @click="closeOthersTags(); closeContextMenu()">
        <i class="el-icon-remove-outline context-icon"></i>关闭其他
      </li>
      <li v-if="hasLeftTags" @click="closeLeftTags(); closeContextMenu()">
        <i class="el-icon-back context-icon"></i>关闭左侧
      </li>
      <li v-if="hasRightTags" @click="closeRightTags(); closeContextMenu()">
        <i class="el-icon-right context-icon"></i>关闭右侧
      </li>
      <li @click="closeAllTags(); closeContextMenu()">
        <i class="el-icon-delete context-icon"></i>关闭全部
      </li>
    </ul>

    <!-- 全局搜索弹窗 -->
    <global-search :visible.sync="showSearch" />
  </el-container>
</template>

<script>
import p from '@/common/privilege'
import AltSidebar from './AltSidebar'
import ResizeMixin from './mixin/ResizeMixin'
import Breadcrumb from './Breadcrumb'
import GlobalSearch from './GlobalSearch'
import { forceAddWatermark } from '@/common/watermark'

// 静态路由图标映射字典，确保页签中的图标和左侧菜单树上的图标绝对一致 (参考截图效果)
const iconMap = {
  '/home': 'home',
  '/p': 'project-diagram',
  '/c/cl': 'plug',
  '/pl': 'sliders-h',
  '/ct': 'tags',
  '/l': 'microchip',
  '/holiday': 'calendar-alt',
  '/menu': 'list-ul',
  '/n': 'sync',
  '/advert/app': 'ad',
  '/advert/channel': 'yen-sign',
  '/mch/group': 'layer-group',
  '/mch/wx': 'comments',
  '/mch/ali': ['fab', 'alipay'],
  '/mch/apple': ['fab', 'apple'],
  '/xs/args/game': 'gamepad',
  '/xs/args/channel': 'link',
  '/xs/args/logic': 'cog',
  '/xs/args/pay': 'coins',
  '/xs/telecom/auth': 'mobile-alt',
  '/ext/config/game': 'puzzle-piece',
  '/ext/config/channel': 'link',
  '/ext/config/template': 'cubes',
  '/company': 'building',
  '/organize': 'users',
  '/ufw': 'memory',
  '/ufw/white': 'passport',
  '/ufw/black': 'passport',
  '/area': 'network-wired',
  '/ufo/group': 'layer-group',
  '/pkg': 'certificate',
  '/wiki/app': 'cube',
  '/wiki/vpc': 'network-wired'
}

export default {
  name: 'Layout',
  components: {
    AltSidebar,
    Breadcrumb,
    GlobalSearch
  },
  mixins: [ResizeMixin],
  data () {
    return {
      appId: this.$store.getters.appId,
      showSearch: false,
      currentTheme: this.$store.getters.theme || 'light',
      // 根据用户要求，不需要使用页面缓存，刷新即重置复原
      visitedViews: [
        { name: '首页', path: '/home', title: '首页', icon: 'home' },
        { name: '游戏管理', path: '/p', title: '游戏管理', icon: 'project-diagram' },
        { name: '渠道列表', path: '/c/cl', title: '渠道列表', icon: 'plug' },
        { name: '同步参数', path: '/n', title: '同步参数', icon: 'sync' },
        { name: '重载NACOS', path: '/nacos', title: '重载NACOS', icon: 'sync-alt' },
        { name: '商户组', path: '/mch/group', title: '商户组', icon: 'layer-group' }
      ],
      contextMenuVisible: false,
      contextMenuLeft: 0,
      contextMenuTop: 0,
      selectedTag: null
    }
  },
  watch: {
    $route: {
      handler (route) {
        this.addView(route)
        this.moveToCurrentTag() // 平滑滚动到激活页签
      },
      immediate: true
    },
    contextMenuVisible (value) {
      if (value) {
        document.body.addEventListener('click', this.closeContextMenu)
      } else {
        document.body.removeEventListener('click', this.closeContextMenu)
      }
    }
  },
  created () {
    this.$store.dispatch('ChangeAppList').then(() => {
      this.appId = this.$store.getters.appId
      this.$store.dispatch('ChangeApp', this.appId).then(() => {})
    })
  },
  mounted () {
    window.addEventListener('keydown', this.handleGlobalKeydown)
    // 初始化主题
    const theme = this.currentTheme
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  computed: {
    device () {
      return this.$store.getters.device
    },
    sidebar () {
      return this.$store.getters.sidebar
    },
    gameOptions () {
      return this.$store.getters.appList
    },
    isMobile () {
      return this.device === 'mobile'
    },
    username () {
      return this.$store.getters.name || 'Admin'
    },
    usernameInitial () {
      return this.username.charAt(0).toUpperCase()
    },
    isMac () {
      return /macintosh|mac os x/i.test(navigator.userAgent)
    },
    hasLeftTags () {
      if (!this.selectedTag) return false
      const index = this.visitedViews.findIndex(v => v.path === this.selectedTag.path)
      return this.visitedViews.slice(0, index).some(v => v.path !== '/home')
    },
    hasRightTags () {
      if (!this.selectedTag) return false
      const index = this.visitedViews.findIndex(v => v.path === this.selectedTag.path)
      return this.visitedViews.slice(index + 1).some(v => v.path !== '/home')
    }
  },
  methods: {
    changeGame (val) {
      this.$store.dispatch('ChangeApp', val).then(() => {
        this.$message.success('已成功切换游戏上下文')
        window.location.reload()
      })
    },
    handleUserCommand (command) {
      if (command === 'logout') {
        this.fedLogOut()
      } else if (command === 'profile') {
        this.$alert(`当前登录用户: <b>${this.username}</b><br/>权限角色: 系统管理员<br/>系统状态: 运行正常`, '个人信息', {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '确定'
        })
      } else if (command === 'shortcuts') {
        this.$alert('键盘快捷键说明：<br/><br/><b>Ctrl + K</b> 或 <b>Cmd + K</b> : 呼出全局搜索菜单<br/><b>Esc</b> : 关闭搜索框', '快捷键参考', {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '确定'
        })
      }
    },
    fedLogOut () {
      this.$confirm('确认退出系统吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$store.dispatch('FedLogOut').then(() => {
          const redirectUri = encodeURIComponent(window.location.origin + '#/login')
          window.location.href = p.OAuthExitUrl + '?redirect_uri=' + redirectUri
        })
      }).catch(() => {})
    },
    handleClickOutside () {
      this.$store.dispatch('CloseSideBar', { withoutAnimation: false })
    },
    handleGlobalKeydown (e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        this.showSearch = !this.showSearch
      }
    },
    toggleTheme () {
      this.$store.dispatch('ToggleTheme').then((nextTheme) => {
        this.currentTheme = nextTheme
        if (nextTheme === 'dark') {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
        // 主动更新水印颜色以匹配当前主题的灰度
        forceAddWatermark()
      })
    },
    isActive (view) {
      return view.path === this.$route.path
    },
    addView (route) {
      if (!route.path) return
      // 过滤登录页、404等，以及 hidden 的隐藏页面
      if (route.path === '/login' || route.path === '/404' || route.meta?.hidden || route.hidden) return

      if (this.visitedViews.some(v => v.path === route.path)) return

      let icon = iconMap[route.path] || 'file-alt'
      let name = route.name || (route.meta && route.meta.title) || route.path

      if (route.matched && route.matched.length > 0) {
        for (let i = route.matched.length - 1; i >= 0; i--) {
          const match = route.matched[i]
          if (match.path === '/home' || match.path === '/') {
            icon = 'home'
            name = '首页'
            break
          }
          if (!iconMap[route.path]) {
            if (match.children && match.children.length > 0) {
              const child = match.children.find(c => c.path === route.path || (match.path + '/' + c.path) === route.path || c.path === route.path.replace(match.path + '/', ''))
              if (child) {
                icon = child.icon || child.meta?.icon || match.icon || match.meta?.icon || icon
                name = child.name || name
                break
              }
            }
            if (match.icon || (match.meta && match.meta.icon)) {
              icon = match.icon || match.meta.icon
              break
            }
          }
        }
      }

      if (name === '工作台') name = '首页'
      if (name === '产品管理') name = '游戏管理'
      if (name === '对接逻辑') name = 'Visitor逻辑'
      if (name === '参数同步') name = '同步参数'
      if (name === '商户组管理') name = '商户组'
      if (name === '支付Args') name = '支付Agrs'
      if (name === '渠道管理') name = '渠道列表'

      const nextView = {
        name: name,
        path: route.path,
        title: name,
        icon: icon
      }

      if (route.path === '/nacos') {
        const syncIndex = this.visitedViews.findIndex(v => v.path === '/n')
        if (syncIndex > -1) {
          this.visitedViews.splice(syncIndex + 1, 0, nextView)
          return
        }
      }

      this.visitedViews.push(nextView)
    },
    isFixed (view) {
      if (!view) return false
      const fixedPaths = ['/home', '/p', '/c/cl', '/n', '/nacos', '/mch/group']
      return fixedPaths.includes(view.path)
    },
    closeView (view) {
      if (this.isFixed(view)) return
      const index = this.visitedViews.findIndex(v => v.path === view.path)
      if (index > -1) {
        this.visitedViews.splice(index, 1)

        if (this.$route.path === view.path) {
          const nextView = this.visitedViews[this.visitedViews.length - 1] || { path: '/home' }
          this.$router.push(nextView.path)
        }
      }
    },
    parseIcon (icon) {
      if (Array.isArray(icon)) return icon
      if (typeof icon === 'string' && icon.includes(' ')) {
        const parts = icon.split(' ')
        const prefix = parts[0].replace('fa-', '')
        const name = parts[1].replace('fa-', '')
        return [prefix, name]
      }
      if (typeof icon === 'string') {
        return icon.replace('fa-', '')
      }
      return icon
    },

    // 页签点击回调，手动控制路由跳转，完美避开 router-link 内部莫名被阻止的缺陷
    handleTagClick (tag) {
      if (this.$route.path !== tag.path) {
        this.$router.push(tag.path)
      }
    },

    // 平滑滚动到激活页签的位置，确保其在可视区域中
    moveToCurrentTag () {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.tags-view-container')
        if (!container) return
        const activeTag = container.querySelector('.tag-view-item.active')
        if (activeTag) {
          const containerWidth = container.offsetWidth
          const containerScrollLeft = container.scrollLeft
          const tagOffsetLeft = activeTag.offsetLeft
          const tagWidth = activeTag.offsetWidth

          if (tagOffsetLeft < containerScrollLeft) {
            // 页签超出了左边界，向左滚动
            container.scrollTo({
              left: tagOffsetLeft - 16,
              behavior: 'smooth'
            })
          } else if (tagOffsetLeft + tagWidth > containerScrollLeft + containerWidth) {
            // 页签超出了右边界，向右滚动
            container.scrollTo({
              left: tagOffsetLeft + tagWidth - containerWidth + 16,
              behavior: 'smooth'
            })
          }
        }
      })
    },

    // 页签右键上下文功能 - 直接使用 Client 物理坐标，不再加相对计算
    openContextMenu (tag, e) {
      this.contextMenuLeft = e.clientX
      this.contextMenuTop = e.clientY
      this.selectedTag = tag
      this.contextMenuVisible = true
    },
    closeContextMenu () {
      this.contextMenuVisible = false
    },
    refreshSelectedTag (view) {
      if (!view) return
      if (this.$route.path !== view.path) {
        this.$router.push(view.path).then(() => {
          window.location.reload()
        })
      } else {
        window.location.reload()
      }
    },
    closeSelectedTag (view) {
      this.closeView(view)
    },
    closeOthersTags () {
      this.visitedViews = this.visitedViews.filter(v => v.path === this.selectedTag.path || this.isFixed(v))
      const isClosed = !this.visitedViews.some(v => v.path === this.$route.path)
      if (isClosed) {
        this.$router.push(this.selectedTag.path)
      }
    },
    closeLeftTags () {
      const index = this.visitedViews.findIndex(v => v.path === this.selectedTag.path)
      if (index > -1) {
        this.visitedViews = this.visitedViews.filter((v, i) => i >= index || this.isFixed(v))
        const isClosed = !this.visitedViews.some(v => v.path === this.$route.path)
        if (isClosed) {
          this.$router.push(this.selectedTag.path)
        }
      }
    },
    closeRightTags () {
      const index = this.visitedViews.findIndex(v => v.path === this.selectedTag.path)
      if (index > -1) {
        this.visitedViews = this.visitedViews.filter((v, i) => i <= index || this.isFixed(v))
        const isClosed = !this.visitedViews.some(v => v.path === this.$route.path)
        if (isClosed) {
          this.$router.push(this.selectedTag.path)
        }
      }
    },
    closeAllTags () {
      this.visitedViews = this.visitedViews.filter(v => this.isFixed(v))
      const isClosed = !this.visitedViews.some(v => v.path === this.$route.path)
      if (isClosed) {
        this.$router.push('/home')
      }
    }
  }
}
</script>

<style scoped>
  .app-layout-container {
    height: 100%;
    background-color: var(--bg-page);
  }
  .app-main-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
  .app-header-component {
    background-color: var(--bg-header);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid var(--border-color);
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.02);
    height: 56px !important;
    line-height: 56px;
    padding: 0 16px;
    z-index: 99;
  }

  /* 页签标签栏 (TagsView) 样式 - 大气的 48px 高度，配合 z-index: 100 !important 防止被遮挡，并添加立体投影隔离感 */
  .tags-view-container {
    height: 48px;
    width: 100%;
    background-color: var(--bg-card);
    border-bottom: 1px solid var(--border-color);
    display: flex;
    align-items: center;
    padding: 0 16px;
    box-sizing: border-box;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 8px;
    z-index: 100 !important;
    flex-shrink: 0;
    position: relative;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03); /* 立体投影隔离 */
  }
  html.dark .tags-view-container {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2); /* 暗色立体投影 */
  }

  .tags-view-container::-webkit-scrollbar {
    height: 3px;
  }
  .tags-view-container::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 2px;
  }
  html.dark .tags-view-container::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
  }

  /* 页签项样式 - 恢复为小巧精致的 26px，使用 flex-shrink: 0 与 white-space: nowrap 防止标签过多被强行压缩和文字重叠换行 */
  .tag-view-item {
    display: inline-flex;
    align-items: center;
    height: 26px;
    line-height: 26px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    color: var(--text-muted);
    background: var(--bg-card);
    padding: 0 10px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    gap: 6px;
    flex-shrink: 0 !important;
    white-space: nowrap !important;
  }
  .tag-view-item:hover {
    background-color: #f1f5f9;
    color: var(--text-main);
    border-color: #cbd5e1;
  }
  html.dark .tag-view-item:hover {
    background-color: #334155;
    color: #f1f5f9;
    border-color: #475569;
  }

  /* 激活项呈正蓝色背景加白色文字 (参考截图二效果) */
  .tag-view-item.active {
    background-color: var(--primary-color) !important;
    color: #ffffff !important;
    border-color: var(--primary-color) !important;
    font-weight: 500;
  }
  html.dark .tag-view-item.active {
    background-color: var(--primary-color) !important;
    color: #ffffff !important;
    border-color: var(--primary-color) !important;
  }

  /* 激活时，子组件（包括 icon 和 close 按钮）也要为白色 */
  .tag-view-item.active .tag-icon {
    color: #ffffff !important;
  }
  .tag-view-item.active .tag-close-btn {
    color: #ffffff !important;
  }
  .tag-view-item.active .tag-close-btn:hover {
    background-color: rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
  }

  .tag-icon {
    font-size: 11px;
    color: inherit;
  }
  .tag-title {
    line-height: 1;
  }
  .tag-close-btn {
    font-size: 10px;
    border-radius: 50%;
    width: 14px;
    height: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    color: var(--text-muted);
    margin-left: 2px;
  }
  .tag-close-btn:hover {
    background-color: rgba(0, 0, 0, 0.08);
    color: #ef4444;
  }
  html.dark .tag-close-btn:hover {
    background-color: rgba(255, 255, 255, 0.15);
    color: #f87171;
  }

  /* 页签右键上下文菜单，具有高层级 z-index: 10000 且设为 fixed 视口绝对定位以防被任何 overflow-y: hidden 裁剪 */
  .contextmenu {
    margin: 0;
    background: #ffffff;
    z-index: 10000 !important;
    position: fixed !important; /* 视口固定定位 */
    list-style-type: none;
    padding: 5px 0;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #333333;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
    min-width: 100px;
  }

  html.dark .contextmenu {
    background: #1e293b;
    color: #f1f5f9;
    border-color: #334155;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3) !important;
  }

  .contextmenu li {
    margin: 0;
    padding: 7px 16px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  .contextmenu li:hover {
    background: #f1f5f9;
    color: var(--primary-color);
  }

  html.dark .contextmenu li:hover {
    background: #334155;
    color: #60a5fa;
  }

  .contextmenu li .context-icon {
    margin-right: 8px;
    font-size: 13px;
    color: #64748b;
    vertical-align: middle;
  }

  .contextmenu li.disabled {
    color: #94a3b8 !important;
    cursor: not-allowed !important;
    background: transparent !important;
  }

  .contextmenu li.disabled .context-icon {
    color: #cbd5e1 !important;
  }

  .app-main-component {
    padding: 16px;
    overflow-y: auto;
    background-color: var(--bg-page);
    flex: 1;
  }
  .flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .header-left {
    display: flex;
    align-items: center;
  }
  .header-right {
    display: flex;
    align-items: center;
  }
  .app-breadcrumb {
    margin-left: 0;
  }
  .drawer-bg {
    background: #000;
    opacity: 0.3;
    width: 100%;
    top: 0;
    height: 100%;
    position: absolute;
    z-index: 999;
  }

  /* 搜索触发按钮 */
  .search-trigger-btn {
    display: flex;
    align-items: center;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0 12px;
    color: #64748b;
    font-size: 12.5px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-right: 16px;
    user-select: none;
    width: 180px;
    height: 32px;
    box-sizing: border-box;
  }
  .search-trigger-btn:hover {
    background: #e2e8f0;
    color: var(--text-main);
    border-color: #cbd5e1;
  }
  .search-btn-icon {
    margin-right: 8px;
    font-size: 11px;
  }
  .search-btn-text {
    flex: 1;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .search-btn-kbd {
    display: flex;
    gap: 2px;
  }
  .kbd-key {
    background: #ffffff;
    border: 1px solid #cbd5e1;
    box-shadow: 0 1px 0 rgba(0,0,0,0.1);
    border-radius: 3px;
    padding: 0px 4px;
    font-size: 9px;
    color: #64748b;
    line-height: 14px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  /* 游戏选择器 */
  .game-select-wrapper {
    margin-right: 16px;
  }
  .premium-game-select {
    width: 180px;
  }
  .premium-game-select >>> .el-input__inner {
    border-radius: 8px;
    background-color: #f1f5f9;
    border-color: #e2e8f0;
    color: var(--text-main);
    font-weight: 500;
    transition: all 0.2s ease;
  }
  .premium-game-select :deep(.el-input__inner:hover) {
    border-color: #cbd5e1;
    background-color: #e2e8f0;
  }
  .premium-game-select >>> .el-input__inner:focus {
    border-color: var(--primary-color);
    background-color: #ffffff;
  }

  /* 主题切换按钮 */
  .theme-switch-btn-wrapper {
    margin-right: 16px;
    display: flex;
    align-items: center;
  }
  .theme-switch-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    cursor: pointer;
    background-color: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #64748b;
    transition: all 0.2s ease;
  }
  .theme-switch-btn:hover {
    background-color: #e2e8f0;
    color: var(--text-main);
    border-color: #cbd5e1;
  }
  .theme-icon {
    font-size: 14px;
  }

  /* 用户下拉 */
  .user-profile-dropdown {
    cursor: pointer;
  }
  .user-avatar-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border-radius: 50%;
    transition: all 0.25s ease;
    height: 36px;
    width: 36px;
  }
  .user-avatar-wrapper:hover {
    background-color: var(--border-color);
  }
  .user-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2563eb, #6366f1, #d946ef);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    border: 1.5px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 4px 10px rgba(99, 102, 241, 0.25), 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .user-avatar-wrapper:hover .user-avatar {
    transform: scale(1.06) rotate(6deg);
    box-shadow: 0 6px 14px rgba(99, 102, 241, 0.35);
  }

  /* Dropdown 弹窗菜单定制 */
  .premium-dropdown-menu {
    border-radius: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
    padding: 6px 0 !important;
    min-width: 150px;
  }
  .dropdown-user-header {
    padding: 10px 16px;
    border-bottom: 1px solid #f4f4f5;
    display: flex;
    flex-direction: column;
    margin-bottom: 4px;
  }
  .header-username {
    font-size: 13.5px;
    font-weight: 600;
    color: #18181b;
  }
  .header-role {
    font-size: 11px;
    color: #71717a;
    margin-top: 2px;
  }
  .dropdown-item-icon {
    margin-right: 8px;
    font-size: 12px;
    color: #71717a;
    width: 14px;
    text-align: center;
  }
  .menu-item-logout {
    color: #ef4444 !important;
  }
  .menu-item-logout:hover {
    background-color: #fef2f2 !important;
    color: #ef4444 !important;
  }
  .menu-item-logout .dropdown-item-icon {
    color: #ef4444 !important;
  }

  /* 暗色下的额外细节微调 */
  html.dark .theme-switch-btn {
    background-color: #1e293b;
    border-color: #334155;
    color: #94a3b8;
  }
  html.dark .theme-switch-btn:hover {
    background-color: #334155;
    color: #f1f5f9;
    border-color: #475569;
  }
  html.dark .search-trigger-btn {
    background-color: #1e293b;
    border-color: #334155;
    color: #94a3b8;
  }
  html.dark .search-trigger-btn:hover {
    background-color: #334155;
    color: #f1f5f9;
    border-color: #475569;
  }
  html.dark .kbd-key {
    background-color: #0f172a;
    border-color: #334155;
    color: #94a3b8;
  }
  html.dark .premium-game-select >>> .el-input__inner {
    background-color: #1e293b;
    border-color: #334155;
    color: #f1f5f9;
  }
  html.dark .premium-game-select :deep(.el-input__inner:hover) {
    border-color: #475569;
    background-color: #334155;
  }
  html.dark .user-avatar-wrapper:hover {
    background-color: #334155;
  }

  /* 平滑的页面切换过渡 */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: opacity 0.2s, transform 0.2s;
  }
  .fade-slide-enter {
    opacity: 0;
    transform: translateY(8px);
  }
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }
</style>
